import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Modal from '@/components/ui/Modal';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import MultiSelectDropdown from '@/components/ui/MultiSelectDropdown';
import {
  faSpinner,
  faExclamationTriangle,
  faTimesCircle,
  faBug,
  faSkull,
  faFilter,
  faRefresh,
  faArrowLeft,
  faCheck,
  faEye,
  faTimes,
  faChevronRight,
  faCircleInfo,
  faCircle,
  faSquare,
} from '@/fontawesome';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import type { ErrorShortLog, ErrorLog, ErrorData } from '@/models';

const pageSize = 50;

export default function AdminErrorsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const errorIdFromUrl = params.get('error');

  const [errors, setErrors] = useState<ErrorShortLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters - default to errors/fatal and unacknowledged
  const [severityFilter, setSeverityFilter] = useState<string[]>(['Err', 'Fatal']);
  const [acknowledgedFilter, setAcknowledgedFilter] = useState<boolean | null>(false);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');

  // Modals
  const [errorDetailModal, setErrorDetailModal] = useState<{open: boolean, errorId: string | null}>({open: false, errorId: null});
  const [bulkAckModal, setBulkAckModal] = useState<{open: boolean, note: string}>({open: false, note: ''});
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [loadingError, setLoadingError] = useState(false);

  // Bulk operations
  const [bulkAckLoading, setBulkAckLoading] = useState(false);

  // Dropdown states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  useEffect(() => {
    fetchErrors();
  }, [severityFilter, acknowledgedFilter, typeFilter, searchFilter, currentPage]);

  // Handle URL-based error loading
  useEffect(() => {
    if (errorIdFromUrl) {
      handleErrorClick(errorIdFromUrl);
    }
  }, [errorIdFromUrl]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.status-dropdown')) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchErrors = async () => {
    try {
      setLoading(true);
      setError(null);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listErrors({
        limit: pageSize,
        next: cursorToken,
        sort: 'created',
        sortDirection: 'DESC',
        acknowledged: acknowledgedFilter ?? undefined,
        severity: severityFilter.length > 0 ? severityFilter.join(',') : undefined,
        type: typeFilter || undefined,
        search: searchFilter || undefined
      });

      setErrors(response.errors);
      setTotal(response.total);
    } catch (err) {
      setError('Fehler beim Laden der Fehler');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleErrorClick = async (errorId: string) => {
    try {
      setLoadingError(true);
      setErrorDetailModal({open: true, errorId});

      // Update URL without reloading the page
      const newParams = new URLSearchParams(location.search);
      newParams.set('error', errorId);
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });

      const errorDetail = await APIRoutes.getError(errorId);
      setSelectedError(errorDetail);
    } catch (err) {
      handleAPIError(err);
      setErrorDetailModal({open: false, errorId: null});

      // Remove error from URL on failure
      const newParams = new URLSearchParams(location.search);
      newParams.delete('error');
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    } finally {
      setLoadingError(false);
    }
  };

  const handleQuickAck = async (errorId: string) => {
    try {
      await APIRoutes.acknowledgeError(errorId);
      showSuccessToast('Fehler wurde bestätigt');

      // Refresh the error list
      await fetchErrors();
    } catch (err) {
      handleAPIError(err);
    }
  };

  const handleBulkAck = async () => {
    try {
      setBulkAckLoading(true);

      const filters = {
        acknowledged: acknowledgedFilter ?? undefined,
        severity: severityFilter.length > 0 ? severityFilter.join(',') : undefined,
        type: typeFilter || undefined,
        search: searchFilter || undefined
      };

      const response = await APIRoutes.acknowledgeMultipleErrors(bulkAckModal.note, filters);
      showSuccessToast(`${response.count} Fehler wurden bestätigt`);

      setBulkAckModal({open: false, note: ''});
      await fetchErrors();
    } catch (err) {
      handleAPIError(err);
    } finally {
      setBulkAckLoading(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'Fatal':
        return <FontAwesomeIcon icon={faSkull} className="w-4 h-4 text-red-700" />;
      case 'Err':
        return <FontAwesomeIcon icon={faTimesCircle} className="w-4 h-4 text-red-500" />;
      case 'Warn':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-yellow-500" />;
      case 'Info':
        return <FontAwesomeIcon icon={faCircleInfo} className="w-4 h-4 text-gray-500" />;
      case 'Debug':
        return <FontAwesomeIcon icon={faBug} className="w-4 h-4 text-gray-500" />;
      case 'Trace':
        return <FontAwesomeIcon icon={faCircle} className="w-4 h-4 text-gray-500" />;
      default:
        return <FontAwesomeIcon icon={faSquare} className="w-4 h-4 text-gray-500" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    switch (severity) {
      case 'FATAL':
        return `${baseClasses} bg-red-200 dark:bg-red-800/50 text-red-900 dark:text-red-200`;
      case 'ERROR':
        return `${baseClasses} bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300`;
      case 'WARN':
        return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300`;
      case 'DEBUG':
        return `${baseClasses} bg-gray-100 dark:bg-gray-900/50 text-gray-800 dark:text-gray-300`;
      default:
        return `${baseClasses} bg-gray-100 dark:bg-gray-900/50 text-gray-800 dark:text-gray-300`;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleSeverityChange = (selected: string[]) => {
    setSeverityFilter(selected);
    setCurrentPage(1);
  };

  const collectErrorChain = (errorData: ErrorData | undefined): ErrorData[] => {
    const chain: ErrorData[] = [];
    let current = errorData;

    while (current) {
      chain.push(current);
      current = current.original;
    }

    return chain;
  };

  const renderMetaValue = (value: unknown): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{String(value)}</span>;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return <span className="text-xs text-gray-700 dark:text-gray-300 break-all whitespace-pre-wrap">{String(value)}</span>;
    }
    return (
      <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto max-h-300 overflow-y-auto whitespace-pre">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  };

  const renderErrorTrace = (errorData: ErrorData | undefined): React.ReactNode => {
    if (!errorData) {
      return null;
    }

    const errorChain = collectErrorChain(errorData);

    return (
      <div className="space-y-4">
        {errorChain.map((error, index) => (
          <div key={index} className="border-l-4 border-blue-200 dark:border-blue-800 pl-4">
            <div className="flex items-center mb-2">
              <FontAwesomeIcon
                icon={index === 0 ? faCircleInfo : faCircle}
                className="w-3 h-3 text-blue-500 mr-2"
              />
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                {index === 0 ? 'Aktueller Fehler' : `Ursprungsfehler ${index}`}
              </span>
            </div>

            <div className="mb-3">
              {error.message && (
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1 whitespace-pre-wrap">
                  {error.message}
                </div>
              )}

              <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                {error.type && (
                  <div>
                    <span className="font-medium">Type:</span> {error.type}
                  </div>
                )}
                {error.category && (
                  <div>
                    <span className="font-medium">Category:</span> {error.category}
                  </div>
                )}
                {error.caller && (
                  <div>
                    <span className="font-medium">Caller:</span> {error.caller}
                  </div>
                )}
                {error.statuscode && (
                  <div>
                    <span className="font-medium">Status Code:</span> {error.statuscode}
                  </div>
                )}
                {error.time && (
                  <div>
                    <span className="font-medium">Time:</span> {new Date(error.time).toLocaleString('de-DE')}
                  </div>
                )}
              </div>

              {error.meta && Object.keys(error.meta).length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">
                    Meta:
                  </div>
                  <pre className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-2 rounded whitespace-pre-wrap break-words">
                    {JSON.stringify(error.meta, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Fehler-Management
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setBulkAckModal({open: true, note: ''})}
              disabled={errors.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-2" />
              Alle bestätigen
            </button>
            <button
              onClick={fetchErrors}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faRefresh} className="w-4 h-4 mr-2" />
              Aktualisieren
            </button>
          </div>
        </div>

        {/* Filter controls */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MultiSelectDropdown
              options={[
                { value: 'Fatal', label: 'Fatal' },
                { value: 'Err', label: 'Err' },
                { value: 'Warn', label: 'Warn' },
                { value: 'Info', label: 'Info' },
                { value: 'Debug', label: 'Debug' },
                { value: 'Trace', label: 'Trace' },
              ]}
              selected={severityFilter}
              onChange={handleSeverityChange}
              label="Schweregrad"
              icon={<FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />}
              placeholder="Alle Schweregrade"
              dropdownClassName="severity-dropdown"
            />

            <div className="relative status-dropdown">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <div className="relative">
                <button
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                >
                  <span>
                    {acknowledgedFilter === null
                      ? 'Alle Status'
                      : acknowledgedFilter
                        ? 'Bestätigt'
                        : 'Unbestätigt'
                    }
                  </span>
                  <div className="flex items-center space-x-1">
                    {acknowledgedFilter !== null && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAcknowledgedFilter(null);
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                      >
                        <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                      </button>
                    )}
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className={`w-3 h-3 transition-transform ${statusDropdownOpen ? 'rotate-90' : ''}`}
                    />
                  </div>
                </button>
                {statusDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
                    <div className="p-1">
                      {[
                        { value: null, label: 'Alle Status' },
                        { value: false, label: 'Unbestätigt' },
                        { value: true, label: 'Bestätigt' }
                      ].map(option => (
                        <button
                          key={option.label}
                          onClick={() => {
                            setAcknowledgedFilter(option.value);
                            setStatusDropdownOpen(false);
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer ${
                            acknowledgedFilter === option.value 
                              ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' 
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fehlertyp
              </label>
              <input
                type="text"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="z.B. APIError, ValidationError"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Suchbegriff
              </label>
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Suche in Fehlermeldungen"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Fehler ({total})
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Schweregrad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Typ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nachricht
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Zeitpunkt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {errors.map((error) => (
                    <tr key={error.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getSeverityIcon(error.severity)}
                          <span className={getSeverityBadge(error.severity)}>
                            {error.severity}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {error.type}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        <div className="max-w-xs truncate" title={error.message}>
                          {error.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(error.timestampError)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleErrorClick(error.id)}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                        >
                          <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-1" />
                          Details
                        </button>
                        {!error.acknowledged && (
                          <button
                            onClick={() => handleQuickAck(error.id)}
                            className="inline-flex items-center px-3 py-1 border border-green-300 dark:border-green-600 text-sm font-medium rounded-md text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 cursor-pointer"
                          >
                            <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-1" />
                            Bestätigen
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {errors.length === 0 && (
              <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                Keine Fehler gefunden.
              </div>
            )}

            {/* Pagination */}
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </div>
        )}

        {/* Error Detail Modal */}
        <Modal
          isOpen={errorDetailModal.open}
          onClose={() => {
            setErrorDetailModal({open: false, errorId: null});
            setSelectedError(null);

            // Remove error from URL when closing modal
            const newParams = new URLSearchParams(location.search);
            newParams.delete('error');
            const newUrl = newParams.toString() ? `${location.pathname}?${newParams.toString()}` : location.pathname;
            navigate(newUrl, { replace: true });
          }}
          title="Fehler-Details"
          icon={<FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-600" />}
          maxWidth="fullscreen"
        >
          <div className="p-6 h-full flex flex-col">
            {loadingError ? (
              <div className="flex justify-center items-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : selectedError ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
                {/* Left Column - Basic Info */}
                <div className="space-y-6 overflow-y-auto pr-2 h-full">
                  {/* Header Info */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Grundinformationen</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Schweregrad</dt>
                        <dd className="mt-1 flex items-center space-x-2">
                          {getSeverityIcon(selectedError.severity)}
                          <span className={getSeverityBadge(selectedError.severity)}>
                            {selectedError.severity}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Typ</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedError.type}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Zeitpunkt</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(selectedError.timestampError)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Kategorie</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedError.category}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Listener Method</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedError.listenerMethod}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ExErr ID</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{selectedError.exerrID}</dd>
                      </div>
                    </div>
                    <div className="mt-4">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ID</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono break-all">{selectedError.id}</dd>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Nachricht</dt>
                    <dd className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded">
                      {selectedError.message}
                    </dd>
                  </div>

                  {/* Acknowledgment */}
                  {selectedError.acknowledged && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Bestätigung</dt>
                      <dd className="text-sm text-gray-900 dark:text-white bg-green-50 dark:bg-green-900/20 p-3 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">Von: {selectedError.acknowledged.userName}</span>
                          <span className="text-xs text-gray-500">{formatDate(selectedError.acknowledged.time)}</span>
                        </div>
                        {selectedError.acknowledged.note && (
                          <div className="text-sm">{selectedError.acknowledged.note}</div>
                        )}
                      </dd>
                    </div>
                  )}

                  {/* Log Meta */}
                  {selectedError.logMeta !== null && selectedError.logMeta !== undefined && Object.keys(selectedError.logMeta).length > 0 && (
                    <div className="space-y-2">
                      {Object.entries(selectedError.logMeta).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">LogMeta: {key}</dt>
                          <dd className="text-sm text-gray-900 dark:text-white bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                            {renderMetaValue(value)}
                          </dd>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Extra */}
                  {selectedError.extra !== null && selectedError.extra !== undefined && Object.keys(selectedError.extra).length > 0 && (
                    <div className="space-y-2">
                      {Object.entries(selectedError.extra).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Extra: {key}</dt>
                          <dd className="text-sm text-gray-900 dark:text-white bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded">
                            {renderMetaValue(value)}
                          </dd>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Column - Error Details */}
                <div className="space-y-6 overflow-y-auto pl-2 h-full">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Fehler-Details</dt>
                    <dd className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded">
                      <div className="mb-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Haupt-Fehler:</div>
                        <div className="text-sm text-gray-900 dark:text-white">{selectedError.error.message}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Error Code: {selectedError.error.errorcode}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Category: {selectedError.error.category}
                        </div>
                      </div>
                      {selectedError.error.__data && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Fehler-Stack:</div>
                          {renderErrorTrace(selectedError.error.__data)}
                        </div>
                      )}
                    </dd>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400">
                Fehler konnte nicht geladen werden.
              </div>
            )}
          </div>
        </Modal>

        {/* Bulk Acknowledge Modal */}
        <Modal
          isOpen={bulkAckModal.open}
          onClose={() => setBulkAckModal({open: false, note: ''})}
          title="Alle Fehler bestätigen"
          icon={<FontAwesomeIcon icon={faCheck} className="w-5 h-5 text-green-600" />}
          maxWidth="lg"
        >
          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Möchten Sie alle aktuell gefilterten Fehler bestätigen? Dies betrifft {total} Fehler.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notiz (optional)
                </label>
                <textarea
                  value={bulkAckModal.note}
                  onChange={(e) => setBulkAckModal({...bulkAckModal, note: e.target.value})}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  placeholder="Grund für die Bestätigung..."
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setBulkAckModal({open: false, note: ''})}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
              >
                Abbrechen
              </button>
              <button
                onClick={handleBulkAck}
                disabled={bulkAckLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {bulkAckLoading ? (
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-2" />
                )}
                Bestätigen
              </button>
            </div>
          </div>
        </Modal>
    </>
  );
}
